import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import StarRateRoundedIcon from "@mui/icons-material/StarRateRounded";
import { Stack, Typography } from "@mui/material";
import { Modal } from "@4uhub/lib4uhub";
import { memo, useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Loading from "../../../components/Loading";
import RadioGroupInputIcon from "../../../components/UI/Inputs/RadioGroupInputIcon";
import useFetch from "../../../hooks/useFetch";
import { ITicketAutomationReceivedRule } from "../../../services/tasySchedMenu.service";
import { ScheduleInsuranceService } from "../../Schedule/Insurance/insurance.service";
import { IInsuranceFavorite } from "../../Schedule/Insurance/model";
import { TAppointment } from "../../Schedule/NewSchedule/schema";
import InsuranceForm from "./InsuranceForm";

interface IInsuranceProps {
  rule?: ITicketAutomationReceivedRule | null;
}

const formatInsuranceName = (insurance: IInsuranceFavorite) =>
  insurance.insuranceName;

const Insurance: React.FC<IInsuranceProps> = ({ rule }) => {
  const [insurances, setInsurances] = useState<IInsuranceFavorite[]>([]);
  const [open, setOpen] = useState(false);

  const { getValues, setValue, watch } = useFormContext<TAppointment>();
  const establishment = watch("establishment");

  const { t } = useTranslation();

  const { sendRequest, loading } = useFetch(ScheduleInsuranceService.list);

  const { sendRequest: onFavorite, loading: fLoading } = useFetch(
    ScheduleInsuranceService.editMain,
  );

  const fetch = useCallback(async () => {
    const { patient, insurance } = getValues();

    if (!patient.individualId) return;

    const { data, success } = await sendRequest({
      IndividualId: patient.individualId,
      TicketAutomationReceivedRuleId: rule?.id,
    });

    if (data && success) {
      if (!insurance) {
        const favorite = data.find((i) => i.main);
        if (favorite) {
          setValue("insurance", {
            id: favorite.code,
            name: formatInsuranceName(favorite),
            code: favorite.code,
            insuranceName: favorite.insuranceName,
            main: favorite.main,
            insuranceIntegrationId: favorite.insuranceIntegrationId,
            categoryIntegrationId: favorite.categoryIntegrationId,
            categoryName: favorite.categoryName,
            individual: favorite.individual,
            individualId: favorite.individualId,
          } as any);
        }
      }
      setInsurances(data);
    }
  }, [sendRequest, getValues, setValue, rule]);

  const onClickStarHandler = useCallback(
    async (selectedInsurance: IInsuranceFavorite) => {
      if (!selectedInsurance) return;
      const { data, success } = await onFavorite({
        insuranceIntegrationId: selectedInsurance.insuranceIntegrationId,
        data: {
          insuranceIntegrationId: selectedInsurance.insuranceIntegrationId,
        },
      });

      if (data && success) {
        fetch();
      }
    },
    [onFavorite, fetch],
  );

  const handleClose = () => {
    setOpen(false);
  };

  const handleSuccess = () => {
    handleClose();
    fetch();
  };

  useEffect(() => {
    fetch();
  }, [fetch]);

  if (loading || fLoading) {
    return <Loading />;
  }

  const patient = getValues("patient");

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        title={`${t("page.schedules.newSchedule.insurance")}`}
        maxDialogWidth="xs"
      >
        <InsuranceForm
          individual={{
            id: patient.individualId || patient.id,
            name: patient.name,
          }}
          rule={rule}
          establishmentCode={establishment?.integrationId}
          onSuccess={handleSuccess}
        />
      </Modal>
      <Stack gap={1}>
        <RadioGroupInputIcon
          name="insurance"
          groupLabel={t("page.schedules.newSchedule.insurance")}
          valueField="code"
          disableBorder
          radios={insurances.map((insurance) => ({
            label: formatInsuranceName(insurance),
            value: {
              id: insurance.code,
              name: formatInsuranceName(insurance),
              code: insurance.code,
              insuranceName: insurance.insuranceName,
              main: insurance.main,
              insuranceIntegrationId: insurance.insuranceIntegrationId,
              categoryIntegrationId: insurance.categoryIntegrationId,
              categoryName: insurance.categoryName,
              individual: insurance.individual,
              individualId: insurance.individualId,
            },
          }))}
          icon={<StarRateRoundedIcon />}
          onIconClick={onClickStarHandler}
        />
        <Stack
          alignItems={"center"}
          direction={"row"}
          onClick={() => setOpen(true)}
          gap={1}
          sx={{
            cursor: "pointer",
            "&:hover": {
              opacity: 0.7,
            },
          }}
        >
          <AddCircleOutlineIcon color="inherit" fontSize="small" />
          <Typography>
            {t("page.schedules.newSchedule.addInsurance")}
          </Typography>
        </Stack>
      </Stack>
    </>
  );
};

export default memo(Insurance);
